import { combineRules } from 'fela';

import type { TRuleWithTheme } from '@/styles/theme';

export const cardShadow = '0px 2px 16px 0px rgba(27, 27, 126, 0.15)';

// Open/close animation duration. Shared between the CSS transitions below and
// the JS unmount delay in UserMenu.tsx so the popup is removed only once the
// collapse has finished playing — the two must stay in sync.
export const MENU_ANIM_MS = 200;

// The sidebar container has 1.5rem horizontal padding; pull the card back out
// to an ~8px gutter (matching the Figma) via negative margins, mirroring the
// escape-the-padding trick the nav items use.
export const root: TRuleWithTheme = () => ({
    marginLeft: '-1rem',
    marginRight: '-1rem',
});

// While open, the popup overlays the card and provides the shadow, so the
// card's own shadow is dropped to avoid the two stacking during the animation.
export const card: TRuleWithTheme<{ open: boolean }> = ({ open, theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    width: '100%',
    minHeight: '48px',
    padding: `${theme.spacing(8)} ${theme.spacing(12)}`,
    border: 'none',
    borderRadius: theme.spacing(8),
    backgroundColor: theme.colors.backgroundsSecondary,
    boxShadow: open ? 'none' : cardShadow,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s ease',
    ':hover': {
        backgroundColor: theme.colors.listItemHoverBg,
    },
});

export const cardHeader: TRuleWithTheme = ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    width: '100%',
    minHeight: '32px',
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
});

// Width is set inline (collapsed = trigger width, expanded = 290px) so it can
// animate in sync with the grid-row height expansion below.
export const menu: TRuleWithTheme = ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: `${theme.spacing(8)} ${theme.spacing(12)}`,
    borderRadius: theme.spacing(8),
    backgroundColor: theme.colors.backgroundsSecondary,
    boxShadow: cardShadow,
    transition: `width ${MENU_ANIM_MS}ms ease`,
});

// Collapsible region holding everything below the header. The single grid row
// animates between 0fr (collapsed) and 1fr (expanded), so it grows to its
// content height without a hardcoded pixel target; the header stays fixed above.
export const itemsWrapper: TRuleWithTheme = () => ({
    display: 'grid',
    gridTemplateRows: '0fr',
    transition: `grid-template-rows ${MENU_ANIM_MS}ms ease`,
});

export const itemsWrapperExpanded: TRuleWithTheme = () => ({
    gridTemplateRows: '1fr',
});

export const itemsInner: TRuleWithTheme = () => ({
    overflow: 'hidden',
    minHeight: 0,
});

export const itemsContent: TRuleWithTheme = ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(16),
    paddingTop: theme.spacing(16),
});

export const itemsGroup: TRuleWithTheme = ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%',
});

export const item: TRuleWithTheme = ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(8),
    width: '100%',
    minHeight: '32px',
    padding: `0 ${theme.spacing(4)}`,
    border: 'none',
    borderRadius: theme.spacing(4),
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    ...theme.typography.ParagraphSmallMedium,
    color: theme.colors.foregroundsPrimary,
    ':hover': {
        backgroundColor: theme.colors.listItemHoverBg,
    },
    ':disabled': {
        cursor: 'not-allowed',
        opacity: 0.6,
    },
});

// The colour itself comes from `Link`'s `color` prop, which it applies in both states.
export const linkItem = combineRules(item, (() => ({
    textDecoration: 'none',
})) as TRuleWithTheme);

export const separator: TRuleWithTheme = ({ theme }) => ({
    height: '1px',
    width: '100%',
    backgroundColor: theme.colors.foregroundsSeparator,
    border: 'none',
});

export const destructiveItem: TRuleWithTheme = ({ theme }) => ({
    color: theme.colors.foregroundsDestructive,
});
